import { createSelector } from 'reselect';

import { SEARCH, SORT } from 'constants/FilterConstants';
import { getSelectedDealerGroupId, getSelectedDealerId } from 'selectors/dealerSelectors';

import { PAGE_SIZE } from '../constants';

/** @returns {import("../reducer").WhitelistReducer} */
const getState = state => state.whitelist;

const getDataState = createSelector(getState, state => state.data);

const getUIState = createSelector(getState, state => state.ui);

export const getWhitelistEntries = createSelector(getDataState, data => data.entities);

export const getWhitelistList = createSelector(getWhitelistEntries, entries => Object.values(entries));

// Dashboard selectors
const getDashboardState = createSelector(getUIState, ui => ui.dashboard);

const getDashboardLoading = createSelector(getDashboardState, dashboard => dashboard.loading);

export const isWhitelistLoading = createSelector(getDashboardLoading, loading => loading.fetching);

const getDeletingIds = createSelector(getDashboardLoading, loading => loading.deleting);

export const isWhitelistDeleting = createSelector(
  getDeletingIds,
  (_, entryId) => entryId,
  (deletingIds, entryId) => deletingIds.includes(entryId),
);

export const getWhitelistPage = createSelector(getDashboardState, dashboard => dashboard.page);

// Filter selectors
export const getFilters = createSelector(getDashboardState, dashboard => dashboard.filters);

export const getSearchFilter = createSelector(getFilters, filters => filters[SEARCH]);

export const getSortFilter = createSelector(getFilters, filters => filters[SORT]);

export const getBaseRequestParams = createSelector(
  getWhitelistPage,
  getSelectedDealerId,
  getFilters,
  ({ size }, dealerId, filters) => ({
    dealerId,
    page: 0,
    size: size || PAGE_SIZE,
    ...filters,
  }),
);

// Modal selectors
const getModalState = createSelector(getUIState, ui => ui.modal);

export const isModalOpen = createSelector(getModalState, modal => modal.open);

export const getModalEntityId = createSelector(getModalState, modal => modal.entityId);

export const isWhitelistCreating = createSelector(getModalState, modal => modal.creating);

export const isWhitelistUpdating = createSelector(getModalState, modal => modal.updating);

export const isWhitelistSubmitting = createSelector(
  isWhitelistCreating,
  isWhitelistUpdating,
  getDeletingIds,
  (creating, updating, deletingIds) => creating || updating || deletingIds.length > 0,
);

const getModalEntity = createSelector(
  getWhitelistEntries,
  getModalEntityId,
  (entities, entityId) => (entityId ? entities[entityId] : null),
);

/**
 * Returns the initial form values for the whitelist modal: the stored entity when editing, only the
 * ids the create request needs otherwise — hence the `Partial`, react-final-form fills the rest in.
 * @returns {Partial<import('../types').WhiteListedPlateFormValues>}
 */
export const getInitialForm = createSelector(
  getModalEntity,
  getSelectedDealerId,
  getSelectedDealerGroupId,
  (entity, dealerId, groupId) => (entity ?? { dealerId, groupId }),
);
